import { type FC, useEffect, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import classnames from "classnames";

import { IconButton } from "common/core/button/icon_button";
import { EXCLUDE_CLASS } from "util/sensitive";

import type { PhotoInfo } from "./util";
import Styles from "./actions.module.scss";

const MESSAGES = defineMessages({
  previousLabel: {
    id: "39aab4ef-fdf3-43de-a176-e5bc98be8101",
    defaultMessage: "Previous image",
  },
  nextLabel: {
    id: "b781aab5-5f1f-41e4-8353-128cdf1a3ad5",
    defaultMessage: "Next image",
  },
  thumbnailAlt: {
    id: "5a76c9e7-3b43-48f8-bf7b-6145c727b903",
    defaultMessage: "Image thumbnail {imageNumber}",
  },
});

export const ImageSelector: FC<{
  imageIndex: number;
  setImageIndex: (index: number) => void;
  images: PhotoInfo[];
}> = ({ imageIndex, setImageIndex, images }) => {
  const intl = useIntl();
  const thumbnailContainerRef = useRef<HTMLDivElement | null>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const parent = thumbnailContainerRef.current;
    const target = thumbnailRefs.current[imageIndex];

    if (parent && target) {
      parent.scrollTo({
        left: target.offsetLeft - parent.offsetLeft,
        behavior: "smooth",
      });
    }
  }, [imageIndex]);
  return (
    <div className={Styles.imageSelector}>
      <IconButton
        onClick={() => setImageIndex(imageIndex - 1)}
        disabled={imageIndex === 0}
        buttonSize="condensed"
        name="caret-left"
        label={intl.formatMessage(MESSAGES.previousLabel)}
        variant="tertiary"
        buttonColor="dark"
        automationId="document-viewer-previous"
      />
      <div className={Styles.frames} ref={thumbnailContainerRef}>
        {images.map((image, index) => (
          <div
            key={`frame-${index}`}
            className={classnames(imageIndex === index && Styles.frameBorder)}
            ref={(el) => (thumbnailRefs.current[index] = el)}
            onClick={() => setImageIndex(index)}
          >
            <img
              className={EXCLUDE_CLASS}
              src={image.src}
              alt={intl.formatMessage(MESSAGES.thumbnailAlt, { imageNumber: index + 1 })}
            />
          </div>
        ))}
      </div>
      <IconButton
        onClick={() => setImageIndex(imageIndex + 1)}
        disabled={imageIndex === images.length - 1}
        buttonSize="condensed"
        name="caret-right"
        label={intl.formatMessage(MESSAGES.nextLabel)}
        variant="tertiary"
        buttonColor="dark"
        automationId="document-viewer-next"
      />
    </div>
  );
};
